import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Authenticated session store.
 *
 * Persists the bearer token in `localStorage` so reloads retain the
 * session. The full user object is kept in-memory; re-hydration triggers
 * a fetch in the auth boundary component. Wave 2 will wire
 * `/v1/auth/login` and `/v1/auth/me`.
 */

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  roles: readonly string[];
  /**
   * G2.1 (round-3) — the user's organisation UUID, returned by
   * `/v1/auth/me`. Persisted on `AuthState` rather than `AuthUser`
   * because it's set at login but may not be known until the /me
   * round-trip completes.
   */
  organizationId?: string;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  /**
   * G2.1 (round-3) — caller's organization UUID, populated from
   * `/v1/auth/me` or the login response. Null until the first
   * authenticated fetch resolves. `ProjectSwitcher` reads this to
   * scope `GET /v1/projects` so the server doesn't 400 on missing
   * `organization_id`.
   */
  organizationId: string | null;
  /**
   * #118 (round-6 close-out) — short-lived WS ticket minted by
   * `POST /v1/auth/ws-ticket`. Refreshed on WS reconnect. When
   * present, WS clients attach it as `?token=…`; the backend
   * validates it against the in-memory ticket store and scopes
   * event fan-out via `ShaktiEvent::visible_to`.
   */
  wsTicket: string | null;
  wsTicketExpiresAt: string | null;
  hydrated: boolean;
  login: (token: string, user: AuthUser) => void;
  setUser: (user: AuthUser) => void;
  setOrganizationId: (orgId: string | null) => void;
  setWsTicket: (token: string | null, expiresAt: string | null) => void;
  logout: () => void;
  markHydrated: () => void;
}

const STORAGE_KEY = 'shakti.auth.v1';

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      organizationId: null,
      wsTicket: null,
      wsTicketExpiresAt: null,
      hydrated: false,
      login: (token, user) =>
        set({
          token,
          user,
          organizationId: user.organizationId ?? null,
          // Reset ws ticket on fresh login; it'll be re-minted on
          // the next WS connect.
          wsTicket: null,
          wsTicketExpiresAt: null,
        }),
      setUser: (user) =>
        set((s) => ({
          user,
          organizationId: user.organizationId ?? s.organizationId,
        })),
      setOrganizationId: (orgId) => set({ organizationId: orgId }),
      setWsTicket: (token, expiresAt) =>
        set({ wsTicket: token, wsTicketExpiresAt: expiresAt }),
      logout: () =>
        set({
          token: null,
          user: null,
          organizationId: null,
          wsTicket: null,
          wsTicketExpiresAt: null,
        }),
      markHydrated: () => set({ hydrated: true }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Only persist the credential material — never the user profile, which
      // may hold PII and is always re-fetched on reload. G2.1 also persists
      // organizationId because re-fetching /v1/auth/me after reload is
      // inevitable but we'd rather not 400 the first listProjects call
      // while the re-fetch is in flight.
      partialize: (state) => ({
        token: state.token,
        organizationId: state.organizationId,
      }),
      onRehydrateStorage: () => (hydratedState, error) => {
        if (error) {
          // eslint-disable-next-line no-console
          console.warn('[shakti] auth rehydration failed', error);
        }
        if (hydratedState) {
          hydratedState.markHydrated();
        }
      },
    },
  ),
);

export function hasRole(role: string): boolean {
  const user = useAuthStore.getState().user;
  return user?.roles.includes(role) ?? false;
}
